import { ChangeEvent, useState, useEffect } from 'react';
import {
  Box,
  Card,
  Icon,
  IconButton,
  Link,
  Text,
  Tooltip,
} from '@nimbus-ds/components';
import {
  ExclamationCircleIcon,
  InfoCircleIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { SubscriptionFrequencyTypeEnum } from '@tiendanube/common/src/domains/subscriptions/enums';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { MetaplanFormProps } from '../MetaplanForm';

const EMPTY_VALUE = '';
const DEFAULT_FREQUENCY = '0';
const DEFAULT_DISCOUNT = 0;
const MIN_OPTIONS = 1;

interface FrequencyCardProps
  extends Pick<MetaplanFormProps, 'values' | 'setFieldValue' | 'errors'> {
  disabled?: boolean;
  cleanErrors?: () => void;
}

function ErrorMessage({ message }: Readonly<{ message: string }>) {
  const t = useTranslationCatalog();
  return (
    <Box display="inline-flex" gap="1">
      <Icon
        color="danger-textLow"
        source={<ExclamationCircleIcon size={12} />}
      />
      <Text color="danger-textLow" fontSize="caption">
        {t(message)}
      </Text>
    </Box>
  );
}

function syncStateWithOptions<
  T extends { frequencyParam: string; discountPercentage: number },
>(
  prev: Record<number, string>,
  options: T[],
  getValue: (option: T) => string,
): Record<number, string> {
  const updated: Record<number, string> = {};
  options.forEach((option, index) => {
    updated[index] = prev[index] !== undefined ? prev[index] : getValue(option);
  });
  return updated;
}

function remapStateAfterRemoval(
  prev: Record<number, string>,
  removedIndex: number,
  newLength: number,
): Record<number, string> {
  const updated: Record<number, string> = {};
  Object.keys(prev).forEach((key) => {
    const oldIndex = parseInt(key, 10);
    if (oldIndex < removedIndex) {
      updated[oldIndex] = prev[oldIndex];
    } else if (oldIndex > removedIndex) {
      updated[oldIndex - 1] = prev[oldIndex];
    }
  });
  for (let i = 0; i < newLength; i++) {
    if (updated[i] === undefined) {
      updated[i] = EMPTY_VALUE;
    }
  }
  return updated;
}

export function FrequencyCard({
  values: { subscriptionOptions },
  setFieldValue,
  errors,
  disabled = false,
  cleanErrors,
}: Readonly<FrequencyCardProps>) {
  const t = useTranslationCatalog();
  const [discountValues, setDiscountValues] = useState<Record<number, string>>(
    {},
  );
  const [frequencyValues, setFrequencyValues] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    setDiscountValues((prev) =>
      syncStateWithOptions(prev, subscriptionOptions, (opt) =>
        opt.discountPercentage === DEFAULT_DISCOUNT
          ? EMPTY_VALUE
          : opt.discountPercentage.toString(),
      ),
    );

    setFrequencyValues((prev) =>
      syncStateWithOptions(prev, subscriptionOptions, (opt) =>
        opt.frequencyParam === DEFAULT_FREQUENCY
          ? EMPTY_VALUE
          : opt.frequencyParam,
      ),
    );
  }, [subscriptionOptions]);

  const getError = (field: string, index: number): string | undefined => {
    if (index >= subscriptionOptions.length) {
      return undefined;
    }
    return (
      errors?.[`subscriptionOptions[${index}].${field}`] ||
      errors?.[`subscriptionOptions.${index}.${field}`]
    );
  };

  const updateSubscriptionOption = (
    index: number,
    updates: Partial<typeof subscriptionOptions[0]>,
  ) => {
    const newOptions = [...subscriptionOptions];
    newOptions[index] = {
      ...newOptions[index],
      ...updates,
    };
    setFieldValue('subscriptionOptions', newOptions);
  };

  const handleFieldChange =
    (index: number, field: 'frequencyParam' | 'discountPercentage') =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const sanitizedValue = event.target.value.replace(/\D/g, '');

      if (field === 'frequencyParam') {
        setFrequencyValues((prev) => ({ ...prev, [index]: sanitizedValue }));
        updateSubscriptionOption(index, {
          frequencyParam:
            sanitizedValue === EMPTY_VALUE ? DEFAULT_FREQUENCY : sanitizedValue,
        });
      } else {
        setDiscountValues((prev) => ({ ...prev, [index]: sanitizedValue }));
        updateSubscriptionOption(index, {
          discountPercentage:
            sanitizedValue === EMPTY_VALUE
              ? DEFAULT_DISCOUNT
              : parseInt(sanitizedValue, 10),
        });
      }
    };

  const handleAddFrequency = () => {
    const newOptions = [
      ...subscriptionOptions,
      {
        frequencyType: SubscriptionFrequencyTypeEnum.EVERY_N_DAYS,
        frequencyParam: DEFAULT_FREQUENCY,
        discountPercentage: DEFAULT_DISCOUNT,
      },
    ];
    setFieldValue('subscriptionOptions', newOptions);
  };

  const handleRemoveFrequency = (index: number) => {
    const newOptions = subscriptionOptions.filter((_, i) => i !== index);

    setDiscountValues((prev) =>
      remapStateAfterRemoval(prev, index, newOptions.length),
    );
    setFrequencyValues((prev) =>
      remapStateAfterRemoval(prev, index, newOptions.length),
    );

    setFieldValue('subscriptionOptions', newOptions);

    if (cleanErrors) {
      cleanErrors();
    }
  };

  return (
    <Card>
      <Card.Header title={t('subscriptions.form.frequency.title')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="1">
          {subscriptionOptions.map((option, index) => {
            const frequencyParamError = getError('frequencyParam', index);
            const discountError = getError('discountPercentage', index);

            return (
              <Box key={index} display="flex" flexDirection="column" gap="3">
                <Box
                  display="flex"
                  flexDirection="row"
                  gap="2"
                  alignItems="flex-end"
                >
                  <Box flex="1">
                    <FormField.Input
                      label={
                        <Box display="flex" alignItems="center" gap="1">
                          <Text fontSize="caption">
                            {t('subscriptions.form.frequency.daysLabel')}
                          </Text>
                          <Tooltip
                            content={t(
                              'subscriptions.form.frequency.daysTooltip',
                            )}
                            position="top"
                          >
                            <Icon
                              source={<InfoCircleIcon size="small" />}
                              color="primary-interactive"
                            />
                          </Tooltip>
                        </Box>
                      }
                      name={`subscriptionOptions.${index}.frequencyParam`}
                      id={`frequency-param-${index}`}
                      value={
                        frequencyValues[index] !== undefined
                          ? frequencyValues[index]
                          : option.frequencyParam === DEFAULT_FREQUENCY
                          ? EMPTY_VALUE
                          : option.frequencyParam
                      }
                      placeholder={DEFAULT_FREQUENCY}
                      onChange={handleFieldChange(index, 'frequencyParam')}
                      append={
                        <Text color="neutral-textLow">
                          {t('subscriptions.form.frequency.daysSuffix')}
                        </Text>
                      }
                      appendPosition="end"
                      disabled={disabled}
                      appearance={frequencyParamError ? 'danger' : undefined}
                    />
                  </Box>

                  <Box flex="1">
                    <FormField.Input
                      label={
                        <Box display="flex" alignItems="center" gap="1">
                          <Text fontSize="caption">
                            {t('subscriptions.form.frequency.discountLabel')}
                          </Text>
                          <Tooltip
                            content={t(
                              'subscriptions.form.frequency.discountTooltip',
                            )}
                            position="top"
                          >
                            <Icon
                              source={<InfoCircleIcon size="small" />}
                              color="primary-interactive"
                            />
                          </Tooltip>
                        </Box>
                      }
                      name={`subscriptionOptions.${index}.discountPercentage`}
                      id={`discount-${index}`}
                      value={
                        discountValues[index] !== undefined
                          ? discountValues[index]
                          : option.discountPercentage === DEFAULT_DISCOUNT
                          ? EMPTY_VALUE
                          : option.discountPercentage.toString()
                      }
                      placeholder={DEFAULT_FREQUENCY}
                      onChange={handleFieldChange(index, 'discountPercentage')}
                      append={<Text color="neutral-textLow">%</Text>}
                      appendPosition="end"
                      disabled={disabled}
                      appearance={discountError ? 'danger' : undefined}
                    />
                  </Box>

                  {subscriptionOptions.length > MIN_OPTIONS && (
                    <Box>
                      <IconButton
                        source={<TrashIcon />}
                        size="2rem"
                        onClick={() => handleRemoveFrequency(index)}
                        disabled={disabled}
                      />
                    </Box>
                  )}
                </Box>
                <Box display="flex" flexDirection="row" gap="2">
                  <Box flex="1">
                    {!!frequencyParamError && (
                      <ErrorMessage message={frequencyParamError} />
                    )}
                  </Box>
                  <Box flex="1">
                    {!!discountError && (
                      <ErrorMessage message={discountError} />
                    )}
                  </Box>
                  {subscriptionOptions.length > MIN_OPTIONS && (
                    <Box width="2rem" />
                  )}
                </Box>
              </Box>
            );
          })}

          <Box>
            <Link
              appearance="primary"
              as="button"
              textDecoration="none"
              onClick={handleAddFrequency}
            >
              <PlusCircleIcon />
              {t('subscriptions.form.frequency.addButton')}
            </Link>
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}
